import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { withBillingWriteAccess } from 'contexts/adminAccess';
import { useDataProvider, useNotify, useRefresh, useVersion } from 'react-admin';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';

import Typography from '@material-ui/core/Typography';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core';

import ActionButton from 'components/buttons/ActionButton';
import { Dialog } from 'components/common';
import { UserDetails } from '../types';
import { Checkbox, Input } from 'components/form';
import formatPrice from 'services/helpers/formatPrice';
import { required } from 'components/form/validationRules';

const useStyles = makeStyles(() => ({
  priceContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    '& .MuiTypography-root': {
      fontWeight: 'bold',
    },
  },
}));

interface Transaction {
  recordId: string;
  id: string;
  amount: number;
  fee: number;
}

export interface Props {
  user: UserDetails;
  selectedTransactions: Transaction[];
  setSelectedTransactions: (value: Transaction[]) => void;
}

enum TabValues {
  Selected = 'Selected',
  All = 'All',
}

const BalanceRefundButton = ({ user, selectedTransactions, setSelectedTransactions }: Props) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const version = useVersion();

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [totalValues, setTotalValues] = useState<{ totalFees: 0; totalAmount: 0 } | null>(null);
  const [type, setType] = useState<TabValues>(TabValues.All);

  const handleChangeTab = (event, value) => {
    setType(value);
  };

  useEffect(() => {
    const loadFees = async () => {
      const { data } = await dataProvider.get('billingFees', { id: user.id });

      setTotalValues(data);
    };

    loadFees();
  }, [dataProvider, user.id, version]);

  useEffect(() => {
    if (selectedTransactions?.length) {
      setType(TabValues.Selected);
      return;
    }

    setType(TabValues.All);
  }, [selectedTransactions?.length]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const refundAll = async ({ refundFee }) => {
    try {
      const { data } = await dataProvider.post('refundAllBalance', { userId: user.id, refundFee });

      if (data.failed.length > 0) {
        notify('Insufficient funds to refund the balance', 'error');
      }

      if (!data.failed.length && !data.success.length) {
        notify(
          'Impossible to refund transactions that were created more than 60 days ago',
          'error'
        );
      }

      refresh();

      return data;
    } catch (error: any) {
      return { [FORM_ERROR]: error?.message ?? error };
    } finally {
      setOpenDialog(false);
    }
  };

  const bulkRefund = async ({ description, refundFee }) => {
    try {
      if (!selectedTransactions?.length) {
        throw new Error('Selected transactions can not be refunded.');
      }

      const transactions: Record<string, number> = {};

      selectedTransactions.forEach(
        (item) =>
          (transactions[item.id] = refundFee ? Number(item.amount) + Number(item.fee) : item.amount)
      );

      const response = await dataProvider.post('bulkRefund', {
        userId: user.id,
        data: {
          description,
          transactions,
          refundFee,
        },
      });

      if (response.data.failed.length > 0) {
        notify('Insufficient funds to refund the balance', 'error');
      }

      if (!response.data.failed.length && !response.data.success.length) {
        notify(
          'Impossible to refund transactions that were created more than 60 days ago',
          'error'
        );
      }

      setSelectedTransactions([]);

      refresh();

      return response;
    } catch (error: any) {
      return { [FORM_ERROR]: error?.message };
    } finally {
      setOpenDialog(false);
    }
  };

  const handleSubmitForm = (values) => {
    if (type === TabValues.Selected) {
      return bulkRefund(values);
    }

    if (type === TabValues.All) {
      return refundAll(values);
    }
  };

  const currentFee = useMemo(() => {
    if (type === TabValues.Selected) {
      return selectedTransactions?.reduce((acc, item) => {
        return acc + (item.fee ?? 0);
      }, 0);
    }

    if (type === TabValues.All) {
      return totalValues?.totalFees;
    }
  }, [totalValues?.totalFees, selectedTransactions, type]);

  const getTotal = useCallback(
    (withFees) => {
      if (type === TabValues.Selected) {
        const selectedAmount = selectedTransactions?.reduce(
          (acc, item) => {
            return {
              amount: acc.amount + (item.amount ?? 0),
              amountWithFees: acc.amountWithFees + (item.amount ?? 0) + (item.fee ?? 0),
            };
          },
          { amount: 0, amountWithFees: 0 }
        );

        return withFees ? selectedAmount.amountWithFees : selectedAmount.amount;
      }

      if (type === TabValues.All) {
        return withFees
          ? (totalValues?.totalAmount ?? 0) + (totalValues?.totalFees ?? 0)
          : totalValues?.totalAmount ?? 0;
      }

      return 0;
    },
    [selectedTransactions, totalValues?.totalAmount, totalValues?.totalFees, type]
  );

  return (
    <>
      <ActionButton
        onClick={handleOpenDialog}
        disabled={!selectedTransactions?.length && !totalValues?.totalAmount}>
        Refund
      </ActionButton>

      {openDialog && (
        <Form
          onSubmit={handleSubmitForm}
          initialValues={{
            refundFee: false,
            description: 'Refund from Rollo Team',
          }}
          render={({ handleSubmit, submitting, submitError, values }) => {
            return (
              <form onSubmit={handleSubmit}>
                <Dialog
                  open={true}
                  title="Refund balance"
                  confirmText="Proceed"
                  onConfirm={handleSubmit}
                  onCancel={handleCloseDialog}
                  submitting={submitting}>
                  {!!selectedTransactions?.length && (
                    <Tabs
                      value={type}
                      indicatorColor="primary"
                      textColor="primary"
                      onChange={handleChangeTab}>
                      <Tab value={TabValues.Selected} label="Selected transactions" />
                      <Tab value={TabValues.All} label="Refund all" />
                    </Tabs>
                  )}

                  {type === TabValues.Selected && (
                    <Input
                      name="description"
                      label="Description"
                      inputProps={{ type: 'text' }}
                      validate={required}
                    />
                  )}

                  {!!currentFee && (
                    <Checkbox
                      name="refundFee"
                      label={`Include fees (${formatPrice(currentFee)})`}
                    />
                  )}

                  <Box className={classes.priceContainer}>
                    <Typography>Total amount:</Typography>
                    <Typography>{formatPrice(getTotal(values.refundFee))}</Typography>
                  </Box>
                </Dialog>
              </form>
            );
          }}
        />
      )}
    </>
  );
};

export default withBillingWriteAccess(BalanceRefundButton);
